import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { X, Plus, ArrowLeft } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";

const COLORS = ["#FFCC00", "#FF69B4", "#20B2AA", "#9370DB"];

export default function PropertyCompare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [allProps, setAllProps] = useState([]);
  const [data, setData] = useState([]);
  const [picker, setPicker] = useState(false);

  const selected = useMemo(() => {
    const q = searchParams.get("locations");
    return q ? q.split(",").filter(Boolean) : [];
  }, [searchParams]);

  // Memoize the picker's available-properties list — filter runs O(n²) each render otherwise.
  const pickerCandidates = useMemo(
    () => allProps.filter(p => !selected.includes(p.location)),
    [allProps, selected]
  );

  useEffect(() => {
    api.get("/mis/properties").then(r => setAllProps(r.data));
  }, []);

  useEffect(() => {
    if (!selected.length) {
      setData([]);
      return;
    }
    api.get(`/mis/compare?locations=${encodeURIComponent(selected.join(","))}`)
      .then(r => setData(r.data.properties));
  }, [selected]);

  const updateSelection = (locs) => {
    const unique = Array.from(new Set(locs)).slice(0, 4);
    setSearchParams(unique.length ? { locations: unique.join(",") } : {});
  };

  const remove = (loc) => updateSelection(selected.filter(l => l !== loc));
  const add = (loc) => { updateSelection([...selected, loc]); setPicker(false); };

  // Build trend chart data: align 12-month sequence per location
  const months = data[0]?.trend12 || [];
  const trendData = months.map((m, i) => {
    const row = { month: m.month_label };
    data.forEach(p => {
      const pm = p.trend12[i];
      row[`${p.location}_occ`] = pm?.occupancy_pct ?? null;
      row[`${p.location}_rev`] = pm ? Math.round(pm.total_revenue / 100000) : null;
      row[`${p.location}_adr`] = pm?.adr ?? null;
      row[`${p.location}_revpab`] = pm?.revpab ?? null;
    });
    return row;
  });

  // Latest month side-by-side
  const latestMetrics = ["occupancy_pct", "adr", "revpab", "capacity_beds"];

  const allFys = Array.from(new Set(data.flatMap(p => p.fy_summary.map(f => f.fy)))).sort();

  return (
    <AppShell title="Head to Head" subtitle="Compare up to 4 stops side-by-side">
      <button onClick={() => navigate("/dashboard")} data-testid="back-btn" className="flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-zinc-500 hover:text-brand-ink mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </button>

      {/* Selector */}
      <div className="bg-white border border-brand-line p-5 mb-4" data-testid="selector-card">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Select up to 4 properties to compare</div>
        <div className="flex flex-wrap gap-2 items-center">
          {selected.map((loc, i) => (
            <span key={loc} data-testid={`selected-${loc}`} className="flex items-center gap-2 bg-brand-ink text-white pl-3 pr-1.5 py-1.5">
              <span className="w-2 h-2" style={{ background: COLORS[i] }} />
              <span className="font-display font-bold text-sm">{loc}</span>
              <button onClick={() => remove(loc)} className="hover:bg-zinc-700 p-0.5" data-testid={`remove-${loc}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {selected.length < 4 && (
            <div className="relative">
              <button onClick={() => setPicker(!picker)} data-testid="add-btn" className="flex items-center gap-2 border-2 border-dashed border-brand-ink px-3 py-1.5 hover:bg-brand-yellow text-sm font-display font-bold">
                <Plus className="w-3 h-3" /> Add property
              </button>
              {picker && (
                <div className="absolute z-10 mt-2 bg-white border-2 border-brand-ink w-72 max-h-96 overflow-auto shadow-2xl" data-testid="picker">
                  {allProps.length > 0 && pickerCandidates.map(p => (
                    <button
                      key={p.location}
                      onClick={() => add(p.location)}
                      data-testid={`pick-${p.location}`}
                      className="w-full text-left px-3 py-2 hover:bg-brand-yellow border-b border-brand-line last:border-0"
                    >
                      <div className="font-display font-semibold text-sm">{p.location}</div>
                      <div className="font-mono-tech text-[10px] text-zinc-500">{p.state} · {p.type} · {p.capacity_beds} beds</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!selected.length && (
        <div className="bg-brand-paper border-2 border-dashed border-brand-line p-12 text-center text-zinc-500 font-mono-tech text-sm uppercase tracking-wide" data-testid="empty-state">
          Pick 2–4 properties from the dropdown to compare
        </div>
      )}

      {selected.length > 0 && data.length > 0 && (
        <>
          {/* Side-by-side latest KPI cards */}
          <div className={`grid grid-cols-1 gap-4 mb-4 ${{1:"md:grid-cols-1",2:"md:grid-cols-2",3:"md:grid-cols-3",4:"md:grid-cols-4"}[data.length] || ""}`} data-testid="kpi-row">
            {data.map((p, i) => (
              <div key={p.location} className="bg-white border-t-4 border-x border-b border-brand-line p-5" style={{ borderTopColor: COLORS[i] }} data-testid={`kpi-card-${p.location}`}>
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.type} · {p.model}</div>
                <h3 className="font-display font-bold text-lg mt-1">{p.location}</h3>
                {p.latest ? (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-wide text-zinc-400">Occ%</div>
                      <div className="font-display font-black text-2xl text-brand-ink">{p.latest.occupancy_pct}%</div>
                    </div>
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-wide text-zinc-400">ADR</div>
                      <div className="font-display font-black text-2xl text-brand-ink">₹{p.latest.adr}</div>
                    </div>
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-wide text-zinc-400">RevPAB</div>
                      <div className="font-display font-black text-2xl text-brand-ink">₹{p.latest.revpab}</div>
                    </div>
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-wide text-zinc-400">Beds</div>
                      <div className="font-display font-black text-2xl text-brand-ink">{p.latest.capacity_beds}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-zinc-500 mt-3">No latest snapshot.</div>
                )}
              </div>
            ))}
          </div>

          {/* Trend charts */}
          {[
            { key: "occ", title: "Occupancy %", suffix: "%" },
            { key: "rev", title: "Revenue (₹L)", suffix: "L" },
            { key: "adr", title: "ADR (₹)", suffix: "" },
            { key: "revpab", title: "RevPAB (₹)", suffix: "" },
          ].map(({ key, title }) => (
            <div key={key} className="bg-white border border-brand-line p-6 mb-4" data-testid={`trend-${key}`}>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">12-month trend</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-4">{title}</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#71717A" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: "#71717A" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#09090B", border: "none", color: "#fff", fontSize: 12 }} />
                    {data.map((p, i) => (
                      <Line
                        key={p.location}
                        type="monotone"
                        dataKey={`${p.location}_${key}`}
                        stroke={COLORS[i]}
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                        name={p.location}
                        connectNulls
                      />
                    ))}
                    <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, textTransform: "uppercase" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}

          {/* FY summary side-by-side table */}
          <div className="bg-white border border-brand-line" data-testid="fy-table">
            <div className="px-6 py-4 border-b border-brand-line">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">FY-wise summary</div>
              <h3 className="font-display font-bold text-lg mt-1">All FYs comparison</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-paper">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Metric</th>
                    <th className="px-4 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">Property</th>
                    {allFys.map(fy => (
                      <th key={fy} className="px-4 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">{fy}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-line">
                  {[
                    { key: "total_revenue_lakhs", label: "Revenue", suffix: "L", prefix: "₹" },
                    { key: "avg_occupancy_pct", label: "Occupancy", suffix: "%", prefix: "" },
                    { key: "avg_adr", label: "ADR", suffix: "", prefix: "₹" },
                    { key: "avg_revpab", label: "RevPAB", suffix: "", prefix: "₹" },
                    { key: "ebitda_lakhs", label: "EBITDA", suffix: "L", prefix: "₹" },
                  ].map(metric =>
                    data.map((p, i) => (
                      <tr key={`${metric.key}-${p.location}`} className="hover:bg-brand-paper" data-testid={`fy-row-${metric.key}-${p.location}`}>
                        <td className="px-4 py-2.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-bold">{metric.label}</td>
                        <td className="px-4 py-2.5 font-display font-semibold flex items-center gap-2">
                          <span className="w-2 h-2 inline-block" style={{ background: COLORS[i] }} />
                          {p.location}
                        </td>
                        {allFys.map(fy => {
                          const f = p.fy_summary.find(x => x.fy === fy);
                          const v = f?.[metric.key];
                          return (
                            <td key={fy} className="px-4 py-2.5 font-mono-tech font-bold">
                              {v != null ? `${metric.prefix}${v}${metric.suffix}` : "—"}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
